/**
 * Portal-rendered tooltip primitive.
 *
 * The body is portaled into `document.body` so it escapes ancestor
 * `overflow: auto` scroll clipping (a z-index bump alone won't help when
 * the parent clips its overflow), and positioned with `position: fixed`
 * from a trigger element's bounding rect.
 *
 * Use `useTooltipState` to wire up hover/focus on the trigger; pass the
 * resulting `pos` to `<Tooltip>` and supply your content as children.
 * Themed variants (e.g. an error/red tooltip in the CSV-import review
 * table) layer on top via the `className` prop.
 *
 * Hover delay and fade are both opt-in per caller, and default off. A
 * tooltip the reader went looking for wants neither; one that sits under
 * content they are only passing over wants both.
 */

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './Tooltip.module.scss';

/**
 * How long a pointer rests on a trigger before its tooltip opens, for the callers that ask
 * for a delay. Long enough that dragging the mouse across a table of badges sets off
 * nothing, short enough that stopping on one still feels like an answer.
 */
export const TOOLTIP_HOVER_DELAY_MS = 1000;

/**
 * Fade length, for the callers that ask for one. Applied inline as the transition duration
 * AND used to schedule the unmount, so the element survives exactly as long as it is still
 * fading — one number rather than a CSS copy and a JS copy that drift apart.
 */
export const TOOLTIP_FADE_MS = 160;

export interface TooltipPos {
  /** Distance from top of viewport, in pixels (CSS `top`). */
  top: number;
  /** Distance from right of viewport, in pixels (CSS `right`). */
  right: number;
}

/**
 * Default anchor: 6px below the trigger, right edge aligned with the
 * trigger's right edge, clamped to >=8px so the tooltip never tucks off
 * the right side on narrow modals.
 */
export function defaultTooltipPos(rect: DOMRect): TooltipPos {
  return { top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) };
}

export interface TooltipStateOptions {
  /**
   * Milliseconds a pointer must rest on the trigger before the tooltip opens. 0 (the
   * default) is the right answer for a trigger that is itself a control, since nobody
   * lands on one of those by accident; a delay is for triggers that are just content.
   */
  hoverDelayMs?: number;
  /**
   * Milliseconds to keep the tooltip mounted after it starts closing, so it can fade out.
   * Pair with `<Tooltip visible>`. 0 (the default) unmounts it on the spot.
   */
  fadeMs?: number;
}

/**
 * Open/position/hide bookkeeping for a portal-rendered tooltip:
 *   - capture the trigger's bounding rect on hover/focus
 *   - hold a hover for `hoverDelayMs` before opening, and drop the pending
 *     open again if the pointer leaves first
 *   - keep the node mounted for `fadeMs` after closing so it can fade
 *   - hide on Escape and on any scroll (so the tooltip doesn't float
 *     orphaned from its anchor as the user scrolls)
 *
 * `active` gates hover/focus — passing `false` makes the handlers no-ops.
 * Callers use this to suppress the tooltip on rows with nothing to say
 * (e.g. a clean import row).
 *
 * Returns `open` (is it mounted) and `shown` (is it at full opacity). With no fade the two
 * are the same thing; with one, `open` outlasts `shown` by the length of the fade.
 */
export function useTooltipState<T extends HTMLElement>(active: boolean, options: TooltipStateOptions = {}) {
  const { hoverDelayMs = 0, fadeMs = 0 } = options;
  const triggerRef = useRef<T>(null);
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const [pos, setPos] = useState<TooltipPos>({ top: 0, right: 0 });
  // Pending hover-open, pending post-fade unmount, and the frame that starts a fade-in.
  const openTimer = useRef<number | undefined>(undefined);
  const unmountTimer = useRef<number | undefined>(undefined);
  const fadeFrame = useRef<number | undefined>(undefined);

  function clearPending() {
    window.clearTimeout(openTimer.current);
    window.clearTimeout(unmountTimer.current);
    if (fadeFrame.current !== undefined) {
      window.cancelAnimationFrame(fadeFrame.current);
    }
    openTimer.current = undefined;
    unmountTimer.current = undefined;
    fadeFrame.current = undefined;
  }

  // Timers outlive the component otherwise, and fire setState on an unmounted one.
  useEffect(() => clearPending, []);

  useEffect(() => {
    if (!open) {
      return;
    }
    // Scroll and Escape skip the fade. The anchor is leaving — a tooltip easing out over it
    // reads as lag, and on scroll it would spend the whole fade detached from its trigger.
    const hide = () => {
      clearPending();
      setShown(false);
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        hide();
      }
    };
    window.addEventListener('scroll', hide, true);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', hide, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  /**
   * Opens immediately, wherever the trigger is right now. Internal: a caller that wants
   * this reaches it by leaving `hoverDelayMs` at 0, which is what `handleEnter` then does.
   */
  function openNow() {
    if (!active) {
      return;
    }
    clearPending();
    // Measured here rather than when the hover started: with a delay between the two, the
    // row may have moved, and a stale rect anchors the tooltip to where the badge was.
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPos(defaultTooltipPos(rect));
    }
    setOpen(true);
    if (!fadeMs) {
      setShown(true);
      return;
    }
    // A transition needs a frame at the old value to animate from, and an element gets none
    // on the frame it mounts. So: mount transparent, then flip on the next frame. Two
    // frames deep because the first only guarantees the render, not that it has painted.
    setShown(false);
    fadeFrame.current = window.requestAnimationFrame(() => {
      fadeFrame.current = window.requestAnimationFrame(() => {
        fadeFrame.current = undefined;
        setShown(true);
      });
    });
  }

  function handleEnter() {
    if (!active) {
      return;
    }
    // Already on screen — either fully open, or partway through fading out and the pointer
    // came back. Either way show it now; a second wait for something already visible reads
    // as a glitch.
    if (!hoverDelayMs || open) {
      openNow();
      return;
    }
    clearPending();
    openTimer.current = window.setTimeout(() => {
      openTimer.current = undefined;
      openNow();
    }, hoverDelayMs);
  }

  function handleLeave() {
    clearPending();
    setShown(false);
    if (!fadeMs) {
      setOpen(false);
      return;
    }
    unmountTimer.current = window.setTimeout(() => {
      unmountTimer.current = undefined;
      setOpen(false);
    }, fadeMs);
  }

  return { triggerRef, open, shown, pos, handleEnter, handleLeave };
}

interface TooltipProps {
  pos: TooltipPos;
  id?: string;
  /** Extra class for caller-supplied theming (e.g. an error variant). */
  className?: string;
  /**
   * Opt into the fade, and say which end of it to be at: `true` fades in, `false` fades
   * out. Drive it from `shown` on a `useTooltipState` configured with `fadeMs`, and keep
   * rendering the tooltip until `open` goes false. Omit the prop for an instant tooltip.
   */
  visible?: boolean;
  children: React.ReactNode;
}

export default function Tooltip({ pos, id, className, visible, children }: TooltipProps) {
  const fading = visible !== undefined;
  const classes = [styles.tooltip, className, fading && styles.fade, fading && visible && styles.fadeVisible]
    .filter(Boolean)
    .join(' ');
  return ReactDOM.createPortal(
    <div
      className={classes}
      style={{ top: pos.top, right: pos.right, transitionDuration: fading ? `${TOOLTIP_FADE_MS}ms` : undefined }}
      role="tooltip"
      id={id}
    >
      {children}
    </div>,
    document.body,
  );
}
